import React, { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';

interface AddSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (section: { title: string; content: string; template?: string }) => void;
  insertAfterSection?: string;
}

const customSectionTemplates = {
  'blank': {
    title: 'Custom Section',
    suggestedContent: ''
  },
  'organizational_change': {
    title: 'Organizational Change Management',
    suggestedContent: 'This section outlines the change approval process, communication requirements, and stakeholder notification procedures.\n\n**Change Approval Process:**\n[Describe the approval workflow]\n\n**Communication Requirements:**\n[Define communication channels]\n\n**Stakeholder Notifications:**\n[List key stakeholders]'
  },
  'training_requirements': {
    title: 'Training and Certification Requirements',
    suggestedContent: '**Required Training Courses:**\n- [Course 1]\n- [Course 2]\n\n**Certification Timeline:**\n[Specify deadlines]\n\n**Competency Validation:**\n[Describe validation process]'
  },
  'metrics_reporting': {
    title: 'Metrics and Reporting',
    suggestedContent: '**Key Performance Indicators:**\n- [KPI 1]: [Target]\n- [KPI 2]: [Target]\n\n**Reporting Frequency:**\n[Daily/Weekly/Monthly]\n\n**Dashboard Locations:**\n[Provide access instructions]'
  }
};

const AddSectionModal: React.FC<AddSectionModalProps> = ({ isOpen, onClose, onAdd, insertAfterSection }) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string>('blank');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  useEffect(() => {
    if (isOpen) {
      const template = customSectionTemplates[selectedTemplate as keyof typeof customSectionTemplates];
      setTitle(template.title);
      setContent(template.suggestedContent);
    }
  }, [selectedTemplate, isOpen]);

  const handleAdd = () => {
    if (!title.trim()) {
      alert('Please enter a section title');
      return;
    }

    onAdd({
      title: title.trim(),
      content: content.trim(),
      template: selectedTemplate
    });

    // Reset
    setSelectedTemplate('blank');
    setTitle('');
    setContent('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Add Custom Section</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Template Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Section Template
            </label>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="blank">Blank Section</option>
              <option value="organizational_change">Organizational Change Management</option>
              <option value="training_requirements">Training & Certification</option>
              <option value="metrics_reporting">Metrics and Reporting</option>
            </select>
          </div>

          {/* Section Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Section Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter section title"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Section Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Section Content
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter section content (can be edited later)"
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              {insertAfterSection
                ? `Will be inserted after section ${insertAfterSection}`
                : 'Will be added at the end'}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Section</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddSectionModal;
